import React, { useState, useCallback } from 'react';
import { DispatchStatuses, SettingKey } from 'src/constants';
import { useFireDispatchContext } from 'src/contexts/fire-dispatch.context';
import { IFireDispatch } from 'src/models/fire-dispatch.model';
import {
  ToastSuccess,
  ToastError,
} from 'src/components/atoms/toaster/toaster.component';
import Lang from 'src/libraries/language';
import { useSettings } from 'src/hooks/setting.hook';
import FireDispatchEmailNotificationView from './fire-dispatch-email-notification.view';

type IProp = {
  data: IFireDispatch;
  secondary?: boolean;
};
const FireDispatchEmailNotification: React.FC<IProp> = ({
  data,
  secondary,
}) => {
  const mailers = useSettings([
    SettingKey.DefaultFromEmail,
    SettingKey.DevEmailRecipient,
    SettingKey.CancelledEmail,
    SettingKey.CompletedEmail,
    SettingKey.DispatchedEmail,
    SettingKey.InFlightEmail,
    SettingKey.LandedEmail,
  ]);

  const { actions } = useFireDispatchContext();
  const [isOpen, setIsOpen] = useState(false);

  const fromEmailAddress = mailers[SettingKey.DefaultFromEmail];

  const emailReceiver = () => {
    switch (data.status) {
      case DispatchStatuses.Cancelled: {
        const receiver = mailers[SettingKey.CancelledEmail];
        return receiver ? receiver.split(',') : [];
      }

      case DispatchStatuses.Completed: {
        const receiver = mailers[SettingKey.CompletedEmail];
        return receiver ? receiver.split(',') : [];
      }

      case DispatchStatuses.Dispatched: {
        const receiver = mailers[SettingKey.DispatchedEmail];
        return receiver ? receiver.split(',') : [];
      }

      case DispatchStatuses.InFlight: {
        const receiver = mailers[SettingKey.InFlightEmail];
        return receiver ? receiver.split(',') : [];
      }

      case DispatchStatuses.Landed: {
        const receiver = mailers[SettingKey.LandedEmail];
        return receiver ? receiver.split(',') : [];
      }

      default: {
        const devEmails = mailers[SettingKey.DevEmailRecipient];
        return devEmails ? devEmails.split(',') : [];
      }
    }
  };

  const getEmailBody = () => {
    switch (data.status) {
      case DispatchStatuses.Cancelled: {
        return Lang.formatString(
          Lang.MSG_DISPATCH_EMAIL_CANCELLED,
          data.tailNumber ?? '',
          data.location ?? ''
        ).toString();
      }

      case DispatchStatuses.Completed: {
        return Lang.formatString(
          Lang.MSG_DISPATCH_EMAIL_COMPLETED,
          data.tailNumber ?? '',
          data.fireName ?? '',
          data.dipCount ?? '',
          data.dropCount ?? '',
          data.waterGallons ?? '',
          data.flightHours ?? ''
        ).toString();
      }

      case DispatchStatuses.Dispatched: {
        return (data.lattitude || data.longitude) &&
          (data.lattitude !== '' || data.longitude !== '')
          ? Lang.formatString(
              Lang.MSG_DISPATCH_EMAIL_DISPATCHED_COORDINATES,
              data.tailNumber ?? '',
              data.location ?? '',
              `${data.lattitude}, ${data.longitude} `
            ).toString()
          : Lang.formatString(
              Lang.MSG_DISPATCH_EMAIL_DISPATCHED,
              data.tailNumber ?? '',
              data.location ?? ''
            ).toString();
      }

      case DispatchStatuses.InFlight: {
        return (data.lattitude || data.longitude) &&
          (data.lattitude !== '' || data.longitude !== '')
          ? Lang.formatString(
              Lang.MSG_DISPATCH_EMAIL_INFLIGHT_COORDINATES,
              data.tailNumber ?? '',
              data.location ?? '',
              `${data.lattitude}, ${data.longitude} `
            ).toString()
          : Lang.formatString(
              Lang.MSG_DISPATCH_EMAIL_INFLIGHT,
              data.tailNumber ?? '',
              data.location ?? ''
            ).toString();
      }

      case DispatchStatuses.Landed: {
        return Lang.formatString(
          Lang.MSG_DISPATCH_EMAIL_LANDED,
          data.tailNumber ?? '',
          data.location ?? ''
        ).toString();
      }

      default: {
        return '';
      }
    }
  };

  const handleSend = useCallback(async () => {
    const receiver = emailReceiver();
    const emailbody = getEmailBody();

    const response = await actions.sendEmailNotificationPOST({
      toEmailIds: receiver,
      coverLetter: emailbody,
      eMailBody: emailbody,
      subjectLine:
        Lang.EMAIL_FIRE_DISPATCH_SUBJECT[data.status ? data.status : ''],
      from: fromEmailAddress,
    });

    if (!response.error) {
      ToastSuccess(Lang.MSG_EMAIL_SENT);
    } else {
      ToastError(Lang.MSG_EMAIL_NOT_SENT);
    }
    setIsOpen(false);
  }, [actions, setIsOpen, data]);

  return (
    <FireDispatchEmailNotificationView
      secondary={secondary}
      isOpen={isOpen}
      handleOpen={(value) => setIsOpen(value)}
      handleSend={handleSend}
    />
  );
};

export default FireDispatchEmailNotification;
